import { faEnvelope, faPhone } from '@fortawesome/pro-regular-svg-icons';
import qs from 'qs';
import React, { useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import NotFoundImg from '../assets/img/404.svg';
import LOBanner from '../assets/img/lo-banner.svg';
import Logo from '../assets/img/one-real-mortgage-logo.svg';
import Button from '../components/Button.tsx';
import DefaultSplashV2 from '../components/DefaultSplashV2.tsx';
import FieldErrorMessage from '../components/inputs/FieldErrorMessage.tsx';
import {
  AuthorizedPersonDetailDto,
  LoansAuthorizedUsersApi,
} from '../openapi/atlantis';
import ErrorService from '../services/ErrorService.ts';
import { getAtlantisConfiguration } from '../utils/OpenapiConfigurationUtils.tsx';
import { formatPhoneNumber } from '../utils/StringUtils.ts';
import { EMAIL_VALIDATIONS } from '../utils/Validations.tsx';
import IconText from '../components/IconText.tsx';
import AvatarV2 from '../components/AvatarV2.tsx';

interface FormData {
  emailAddress: string;
}

interface LoanAdvisorRouteProps {}

const LoanAdvisorRoute: React.FC<LoanAdvisorRouteProps> = () => {
  const navigate = useNavigate();
  const { slug }: any = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [authorizedPerson, setAuthorizedPerson] =
    useState<AuthorizedPersonDetailDto>();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const fetchAuthorizedPersonBySlug = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await new LoansAuthorizedUsersApi(
        await getAtlantisConfiguration(),
      ).getLAuthorizedPersonBySlug(slug);
      setAuthorizedPerson(data);
    } catch (e: any) {
      ErrorService.notifyIgnoreNotFound('Unable to fetch authorized person', e);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchAuthorizedPersonBySlug();
  }, [fetchAuthorizedPersonBySlug]);

  const onSubmit = (values: FormData) => {
    const url = `/signup?${qs.stringify({
      emailAddress: values.emailAddress,
      inviterAuthorizedPersonId: authorizedPerson?.id,
    })}`;

    navigate(url);
  };

  if (loading) {
    return (
      <div className='h-screen w-screen'>
        <DefaultSplashV2 />
      </div>
    );
  }

  if (!authorizedPerson) {
    return (
      <div className='flex flex-col bg-reskin-indigo-dark h-screen w-screen overflow-scroll'>
        <div className='flex flex-row items-center justify-between lg:px-40 p-4'>
          <img src={Logo} alt='Logo' className='cursor-pointer' />
        </div>
        <div className='flex flex-col justify-center items-center flex-1  p-4'>
          <img src={NotFoundImg} alt='404' width={400} height={400} />
          <p className='text-center font-inter-medium text-white text-2xl mt-5'>
            Hmm...we can&apos;t seem to find that.
          </p>
          <div className='mt-5'>
            <Button
              label='Go Home'
              fullWidth={isMobile}
              gradientVariant='mintyfresh'
              onPress={() => navigate('/')}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-reskin-midnight-green-900 h-screen w-screen overflow-scroll'>
      <div className='flex flex-row items-center justify-between lg:mx-40 lg:my-10 m-4'>
        <img src={Logo} alt='Logo' className='cursor-pointer' />
      </div>
      <div className='grid lg:grid-cols-2 grid-cols-1 gap-10 lg:pl-40 md:my-10'>
        <div className='flex flex-col-reverse md:flex-col md:gap-0 lg:mx-0 lg:mt-10 m-5 lg:max-w-xl'>
          <div>
            <PartnerProfileCard authorizedPerson={authorizedPerson} />
          </div>
          <div>
            <div className='md:mt-18 space-y-2'>
              <p className='font-inter-light text-[26px] md:text-[42px] text-reskin-aqua md:mt-10 leading-8 md:leading-[50px]'>
                Your Homebuying Journey Starts Here!
              </p>
              <p className='font-inter-light text-white text-base md:text-lg'>
                Enter your email to apply for your mortgage today.
              </p>
            </div>
            <div className='mt-8 '>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className='flex flex-row md:w-[405px] w-full'
              >
                <input
                  type='email'
                  placeholder='Your email address'
                  className='py-2 md:py-4 px-3 font-inter-regular text-base md:w-1/3 rounded-l-[4px] flex-1'
                  {...register('emailAddress', {
                    required: 'Please enter email address',
                    ...EMAIL_VALIDATIONS,
                  })}
                />
                <button
                  type='submit'
                  className='px-4 md:px-8 py-2 md:py-4 bg-reskin-midnight-green-700 rounded-r'
                >
                  <span className='font-inter-regular text-white md:text-base text-sm'>
                    Get Started!
                  </span>
                </button>
              </form>
              {!!errors.emailAddress?.message && (
                <div className='mt-2'>
                  <FieldErrorMessage
                    message={errors.emailAddress.message as string}
                  />
                </div>
              )}
            </div>
            <div className='mt-4'>
              <p className='font-inter-regular text-sm md:text-base text-white'>
                Already have an account?
                <span
                  onClick={() =>
                    navigate(
                      `/login?inviterAuthorizedPersonId=${authorizedPerson?.id}`,
                    )
                  }
                  className='text-reskin-aqua font-inter-medium text-base cursor-pointer ml-1'
                >
                  Sign In
                </span>
              </p>
            </div>
          </div>
        </div>
        <div className='flex flex-row justify-end ml-5'>
          <img src={LOBanner} alt='logo' className='object-contain' />
        </div>
      </div>
    </div>
  );
};
export default LoanAdvisorRoute;

const PartnerProfileCard = ({
  authorizedPerson,
}: {
  authorizedPerson: AuthorizedPersonDetailDto;
}) => (
  <div className='flex flex-col gap-2 lg:gap-3  bg-reskin-midnight-green-800 p-6 lg:py-6 lg:px-11 w-[100%] lg:w-[355px] rounded-[30px]'>
    <div className='flex items-start'>
      <AvatarV2
        name={`${authorizedPerson?.firstName} ${authorizedPerson?.lastName}`}
        imageUrl={authorizedPerson?.avatar}
      />
    </div>
    <div className='text-left'>
      <div>
        <p className='font-inter font-light lg:font-medium text-sm lg:text-2xl text-white mt-1.5'>
          {authorizedPerson?.firstName} {authorizedPerson?.lastName}
        </p>
        <p className='text-sm font-inter font-light text-reskin-regent-400 mt-1.5'>
          {authorizedPerson?.title} - NMLS: #{authorizedPerson?.nmlsNumber}
        </p>
      </div>
      <div className='pt-4 flex flex-col gap-2'>
        <IconText
          onClick={() => {
            window.open(`mailto:${authorizedPerson?.emailAddress}`);
          }}
          icon={faEnvelope}
          label={authorizedPerson?.emailAddress}
          containerStyle='gap-2.5'
        />
        <IconText
          onClick={() => {
            window.open(`tel:${authorizedPerson?.phoneNumber}`);
          }}
          icon={faPhone}
          label={formatPhoneNumber(authorizedPerson?.phoneNumber!)}
          containerStyle='gap-2.5'
        />
      </div>
    </div>
  </div>
);
